import { Search, X } from 'lucide-react';
import type { ChPostPlatform, ChPostContentType, ChPostStatus } from '../types';
import { PLATFORM_LABELS, CONTENT_TYPE_LABELS, POST_STATUS_LABELS } from '../types';

export interface PostFilterState {
  search: string;
  platform: ChPostPlatform | '';
  content_type: ChPostContentType | '';
  status: ChPostStatus | '';
  /** A person responsible (profile id). */
  assignee: string;
}

export const EMPTY_POST_FILTERS: PostFilterState = { search: '', platform: '', content_type: '', status: '', assignee: '' };

interface Props {
  filters: PostFilterState;
  onChange: (filters: PostFilterState) => void;
  /** The team, for the "Responsável" filter. */
  people?: { id: string; name: string }[];
  currentUserId?: string;
}

export function PostFilters({ filters, onChange, people = [], currentUserId }: Props) {
  const hasFilters = filters.platform || filters.content_type || filters.status || filters.search || filters.assignee;

  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <div className="relative flex-1 min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          className="ch-input pl-9 h-9 text-sm"
          placeholder="Pesquisar posts..."
          value={filters.search}
          onChange={e => onChange({ ...filters, search: e.target.value })}
        />
      </div>

      <div className="flex gap-2 flex-wrap">
        <select
          className="ch-select h-9 text-xs font-medium w-auto min-w-[120px]"
          value={filters.platform}
          onChange={e => onChange({ ...filters, platform: e.target.value as ChPostPlatform | '' })}
        >
          <option value="">Plataforma</option>
          {Object.entries(PLATFORM_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>

        <select
          className="ch-select h-9 text-xs font-medium w-auto min-w-[110px]"
          value={filters.content_type}
          onChange={e => onChange({ ...filters, content_type: e.target.value as ChPostContentType | '' })}
        >
          <option value="">Tipo</option>
          {Object.entries(CONTENT_TYPE_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>

        <select
          className="ch-select h-9 text-xs font-medium w-auto min-w-[110px]"
          value={filters.status}
          onChange={e => onChange({ ...filters, status: e.target.value as ChPostStatus | '' })}
        >
          <option value="">Estado</option>
          {Object.entries(POST_STATUS_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>

        {people.length > 0 && (
          <select
            className="ch-select h-9 text-xs font-medium w-auto min-w-[130px]"
            value={filters.assignee}
            onChange={e => onChange({ ...filters, assignee: e.target.value })}
          >
            <option value="">Responsável</option>
            {currentUserId && people.some(p => p.id === currentUserId) && <option value={currentUserId}>Os meus</option>}
            {people.filter(p => p.id !== currentUserId).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        )}

        {hasFilters && (
          <button
            onClick={() => onChange(EMPTY_POST_FILTERS)}
            className="ch-btn ch-btn-ghost ch-btn-sm text-red-500"
          >
            <X className="w-3.5 h-3.5" />
            Limpar
          </button>
        )}
      </div>
    </div>
  );
}
